import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { instance } from '~/utils/api/api';
import PromotionForm from '~/components/PromotionForm/PromotionForm';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { Spinner } from '~/ui';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { ICreatePromotionProps } from '../CreatePromotion/CreatePromotion';
import { getPaginate } from '~/utils/getPaginate';

const EditPromotionPage = () => {
  const { stockId } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { t } = useTranslation();
  const { value, paginate } = getPaginate();

  const { mutateAsync: updatePromotionMethod } = useMutation(
    async ({
      name,
      description,
      start_date,
      end_date,
      burn_count_days,
      calculated_points,
      piwigo_image_id,
      accommodation_ids,
      organization_ids,
      rules,
    }: ICreatePromotionProps) => {
      const response = await instance.put(`admin/promo-loyalty/${stockId}`, {
        loyalty_promo: {
          name,
          description,
          burn_count_days,
          start_date,
          end_date,
          calculated_points,
          piwigo_image_id,
          accommodation_ids,
          organization_ids,
        },
        rules,
      });

      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['promotion']);
        toastSuccess(t('stock_successfull_updated'));
        navigate(`/promotions?${value}=${paginate}`);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(`admin/promo-loyalty/${stockId}`);
      return data;
    },
    queryKey: ['promotion', stockId],
    onError: error => {
      toastError(error);
    },
  });

  return (
    <Page heading={<Heading text={t('edit_promotion')} />}>
      {!isLoading ? (
        <PromotionForm
          isEdit
          data={data}
          onSubmit={updatePromotionMethod}
        />
      ) : (
        <Spinner />
      )}
    </Page>
  );
};

export default EditPromotionPage;
